// Submitted pricing requests: a filterable table where each row links to the
// full detail page. Read-only — customer submissions are never edited here.
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { fetchInquiries } from '../../../store/admin-inquiries-slice.js';
import {
  estimateText, areaText, formatDateTime, statusColor, areaTypeLabel,
} from '../../../utils/admin-inquiry-format.js';

// "submitted" is intentionally omitted: it's a transient state between row
// insert and the sales-notification email resolving, so a persisted "submitted"
// row is a rare crash artifact rather than a normal status worth filtering on.
// Such rows still appear under "All".
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'emailed', label: 'Emailed' },
  { value: 'email_failed', label: 'Email failed' },
];

const InquiriesTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, listLoading, listError } = useSelector((s) => s.adminInquiries);
  const [status, setStatus] = useState('');

  useEffect(() => {
    dispatch(fetchInquiries({ status: status || undefined }));
  }, [dispatch, status]);

  const renderBody = () => {
    if (listLoading && list.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading inquiries" />
        </Box>
      );
    }
    if (list.length === 0) {
      return <Alert severity="info">No inquiries match this filter.</Alert>;
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Received</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Area</TableCell>
            <TableCell>Estimate</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((inq) => (
            <TableRow
              key={inq.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/admin/inquiries/${inq.id}`)}
            >
              <TableCell>{formatDateTime(inq.created_at)}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{inq.contact_name}</Typography>
                <Typography variant="caption" color="text.secondary">{inq.contact_email}</Typography>
              </TableCell>
              <TableCell>{inq.contact_org}</TableCell>
              <TableCell>
                {areaTypeLabel(inq.area_type)}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {areaText(inq.area_sq_mi)}
                </Typography>
              </TableCell>
              <TableCell>{estimateText(inq.estimated_low, inq.estimated_high)}</TableCell>
              <TableCell>
                <Chip label={inq.status} size="small" color={statusColor(inq.status)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      <Stack direction="row" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_e, next) => { if (next !== null) setStatus(next); }}
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((f) => (
            <ToggleButton key={f.value || 'all'} value={f.value}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {renderBody()}
    </Box>
  );
};

export default InquiriesTable;
